import { Autocomplete, TextField, Chip, CircularProgress } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

interface Slot {
  slotId: string;
  name: string;
  [key: string]: any;
}

interface Product {
  productId: string;
  [key: string]: any;
}

interface Menu {
  slots?: Slot[];
  [key: string]: any;
}

interface SlotSelectProps {
  value: Slot | null | Slot[];
  onChange: (slot: Slot | null | Slot[]) => void;
  product?: Product | null;
  menu?: Menu | null;
  multiple?: boolean;
  /** When provided, bypasses internal derivation and uses these slots directly */
  slots?: Slot[];
}

export function SlotSelect({
  value,
  onChange,
  product,
  menu,
  multiple = false,
  slots: externalSlots,
}: SlotSelectProps) {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // When external slots are provided, use them directly and auto-clear invalid selections
  useEffect(() => {
    if (externalSlots === undefined) return;
    setSlots(externalSlots);
    // Clear selection if current value is no longer in the available slots
    if (!multiple) {
      const v = value as Slot | null;
      if (v && !externalSlots.some(s => s.slotId === v.slotId)) {
        onChangeRef.current(null);
      }
    }
  }, [externalSlots, multiple]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (externalSlots !== undefined) return; // skip internal derivation when overridden

    setSlotsLoading(true);

    // If no product is provided, try to get all slots from the menu
    if (!product && menu && menu.slots && Array.isArray(menu.slots)) {
      setSlots(menu.slots);
      setSlotsLoading(false);
      return;
    }

    // Extract slots from the product's variants
    if (product && product.variants && Array.isArray(product.variants)) {
      const selectedVariant = product.selectedVariant || product.variants[0];

      if (selectedVariant && selectedVariant.slots && Array.isArray(selectedVariant.slots)) {
        const extractedSlots = selectedVariant.slots.map((slotItem: any) => slotItem.slot).filter(Boolean);
        setSlots(extractedSlots);
      } else {
        setSlots([]);
      }
    } else {
      setSlots([]);
    }

    setSlotsLoading(false);
  }, [product, menu, externalSlots]);

  // Always use array for value in multiple mode, and ensure value matches options
  const normalizedValue = multiple
    ? Array.isArray(value) ? value.filter(v => slots.some(s => s.slotId === v.slotId)) : value ? [value] : []
    : (value && slots.some(s => s.slotId === (value as Slot).slotId)) ? value : null;

  return (
    <Autocomplete
      options={Array.isArray(slots) ? slots : []}
      getOptionLabel={(option: Slot) => {
        if (!option) return 'No slot';
        if (!option.name && !option.slotCode) return 'Unknown';
        return `${option.name ?? 'Unknown'} (${option.slotCode ?? 'Unknown'})`;
      }}
      isOptionEqualToValue={(option, val) => {
        if (!option || !val) return false;
        return option.slotId === (val as Slot).slotId;
      }}
      renderOption={(props, option) => {
        if (!option) return null;
        const label = (!option.name && !option.slotCode)
          ? 'Unknown'
          : `${option.name ?? 'Unknown'} (${option.slotCode ?? 'Unknown'})`;
        return (
          <li {...props} key={option.slotId || `slot-${Math.random()}`}>
            {label}
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) => {
        if (!tagValue || !Array.isArray(tagValue)) return [];
        return (tagValue as Slot[]).map((option, index) => {
          if (!option) return null;
          const label = (!option.name && !option.slotCode)
            ? 'Unknown'
            : `${option.name ?? 'Unknown'} (${option.slotCode ?? 'Unknown'})`;
          return (
            <Chip
              {...getTagProps({ index })}
              key={option.slotId || `slot-tag-${index}`}
              size="small"
              label={label}
            />
          );
        });
      }}
      value={normalizedValue}
      onChange={(_, newValue) => {
        if (multiple) {
          onChange(Array.isArray(newValue) ? newValue : newValue ? [newValue] : []);
        } else {
          onChange(newValue ?? null);
        }
      }}
      multiple={multiple}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Slot"
          placeholder="Search for slots..."
          fullWidth
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {slotsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
